/**
 * SuggestionBar - Always-on next question suggestions
 *
 * Displays clickable suggestion chips after AI responses.
 * Clicking a suggestion sends that query to the chat.
 *
 * Schema format:
 * {
 *   type: 'suggestion-bar',
 *   suggestions: [
 *     { text: 'Compare with CrowdStrike', query: '...', icon: 'compare' },
 *     { text: 'Show spend trend', query: '...', icon: 'chart' },
 *     { text: 'Export to report', action: 'export', icon: 'download' }
 *   ],
 *   variant: 'default' | 'compact' | 'floating'
 * }
 */

import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  TrendingUp,
  BarChart3,
  Download,
  GitCompare,
  Search,
  FileText,
  Zap,
  ArrowRight,
  RefreshCw,
  Filter,
  Table,
  PieChart,
} from 'lucide-react';
import './SuggestionBar.css';

// Icon mapping for suggestion types
const ICONS = {
  sparkles: Sparkles,
  trend: TrendingUp,
  chart: BarChart3,
  download: Download,
  compare: GitCompare,
  search: Search,
  report: FileText,
  action: Zap,
  next: ArrowRight,
  refresh: RefreshCw,
  filter: Filter,
  table: Table,
  pie: PieChart,
  default: Sparkles,
};

/**
 * SuggestionBar Component
 *
 * @param {Object} props
 * @param {Array} props.suggestions - Array of suggestion objects
 * @param {string} props.variant - 'default' | 'compact' | 'floating'
 * @param {string} props.label - Optional label before suggestions
 * @param {boolean} props.animate - Animate suggestions in
 * @param {function} props.onAction - Callback when suggestion clicked
 */
export default function SuggestionBar({
  suggestions = [],
  variant = 'default',
  label,
  animate = true,
  onAction,
  children,
}) {
  const [visible, setVisible] = useState(!animate);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Animate in on mount
  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => setVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [animate]);

  // Handle suggestion click
  const handleClick = (suggestion, index) => {
    if (onAction) {
      onAction({
        type: 'suggestion',
        text: suggestion.text,
        query: suggestion.query,
        action: suggestion.action,
        index,
      });
    }
  };

  // Don't render if no suggestions
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <div
      className={`suggestion-bar suggestion-bar--${variant} ${visible ? 'suggestion-bar--visible' : ''}`}
    >
      {label && <span className="suggestion-bar__label">{label}</span>}

      <div className="suggestion-bar__chips">
        {suggestions.map((suggestion, index) => {
          const IconComponent = ICONS[suggestion.icon] || ICONS.default;
          const isHovered = hoveredIndex === index;

          return (
            <button
              key={index}
              className={`suggestion-chip ${isHovered ? 'suggestion-chip--hover' : ''}`}
              onClick={() => handleClick(suggestion, index)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                animationDelay: animate ? `${index * 50}ms` : '0ms',
              }}
            >
              <IconComponent className="suggestion-chip__icon" size={14} />
              <span className="suggestion-chip__text">{suggestion.text}</span>
              {suggestion.action && (
                <span className="suggestion-chip__action-badge">
                  {suggestion.action === 'export' ? '↓' : '→'}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {children}
    </div>
  );
}

/**
 * SuggestionChip - Individual suggestion chip (for custom layouts)
 */
export function SuggestionChip({
  text,
  query,
  action,
  icon = 'default',
  variant = 'default',
  onAction,
}) {
  const IconComponent = ICONS[icon] || ICONS.default;

  const handleClick = () => {
    if (onAction) {
      onAction({ type: 'suggestion', text, query, action });
    }
  };

  return (
    <button
      className={`suggestion-chip suggestion-chip--${variant}`}
      onClick={handleClick}
    >
      <IconComponent className="suggestion-chip__icon" size={14} />
      <span className="suggestion-chip__text">{text}</span>
    </button>
  );
}

/**
 * QuickActions - Compact action buttons (for toolbars)
 */
export function QuickActions({
  actions = [],
  onAction,
}) {
  return (
    <div className="quick-actions">
      {actions.map((action, index) => {
        const IconComponent = ICONS[action.icon] || ICONS.action;

        return (
          <button
            key={index}
            className="quick-action"
            onClick={() => onAction?.({ type: 'quick-action', ...action })}
            title={action.text}
          >
            <IconComponent size={16} />
          </button>
        );
      })}
    </div>
  );
}
